import { DestroyRef, DOCUMENT, inject } from "@angular/core";
import { secondsToMilliseconds } from "date-fns";

export interface RefreshSchedulerOptions {
    getLastRefreshed?: () => number | undefined;
}

export function createRefreshScheduler(intervalMs: number, refresh: () => void, options?: RefreshSchedulerOptions): { reset: () => void } {
    const document = inject(DOCUMENT);
    const destroyRef = inject(DestroyRef);

    let lastRefreshed = options?.getLastRefreshed?.() ?? Date.now();
    let refreshTimeout: ReturnType<typeof setTimeout> | undefined;
    let destroyed = false;

    const getNextRefreshDelay = (): number => {
        const persistedRefresh = options?.getLastRefreshed?.() ?? 0;
        const mostRecentRefresh = Math.max(persistedRefresh, lastRefreshed);
        const remaining = intervalMs - (Date.now() - mostRecentRefresh);
        return Math.max(remaining, secondsToMilliseconds(1));
    };

    const tick = (): void => {
        if (document.visibilityState === "hidden") return;
        lastRefreshed = Date.now();
        refresh();
    };

    const scheduleNextRefresh = (): void => {
        if (destroyed) return;
        clearTimeout(refreshTimeout);
        if (document.visibilityState === "hidden") return;

        refreshTimeout = setTimeout(() => {
            try {
                tick();
            } finally {
                scheduleNextRefresh();
            }
        }, getNextRefreshDelay());
    };

    document.addEventListener("visibilitychange", scheduleNextRefresh);

    destroyRef.onDestroy(() => {
        destroyed = true;
        clearTimeout(refreshTimeout);
        document.removeEventListener("visibilitychange", scheduleNextRefresh);
    });
    scheduleNextRefresh();

    return {
        reset: (): void => {
            if (!options?.getLastRefreshed) lastRefreshed = Date.now();
            scheduleNextRefresh();
        }
    };
}
